import { useState, useEffect } from "react";
import { InventorySidebar } from "./InventorySidebar";
import { InventoryContent } from "./InventoryContent";
import { ScrollArea } from "@/components/ui/scroll-area";
import { useIsMobile } from "@/hooks/use-mobile";
import { Button } from "@/components/ui/button";
import { Filter, X } from "lucide-react";
import { InventoryHeader } from "./InventoryHeader";
import { 
  Sheet, 
  SheetContent, 
  SheetTrigger 
} from "@/components/ui/sheet";
import { useTranslation } from 'react-i18next';

interface InventoryLayoutProps {
  title?: string;
}

export function InventoryLayout({ title = "Inventory" }: InventoryLayoutProps) {
  const [selectedCategory, setSelectedCategory] = useState<string>("All");
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [sortOrder, setSortOrder] = useState<string>("name_asc");
  const [filterCategory, setFilterCategory] = useState<string>("all");
  const [isMobileSidebarOpen, setIsMobileSidebarOpen] = useState(false);
  
  const isMobile = useIsMobile();
  const { t } = useTranslation();

  const handleCategorySelect = (category: string) => {
    setSelectedCategory(category);
    if (isMobile) {
      setIsMobileSidebarOpen(false);
    }
  };

  const handleAddItem = () => {
    setIsFormOpen(true);
  };

  const handleCloseForm = () => {
    setIsFormOpen(false);
  };

  return (
    <div className="container mx-auto px-4 max-w-full">
      <InventoryHeader
        searchQuery={searchTerm}
        onSearchChange={setSearchTerm}
        onAddItem={handleAddItem}
        onSortChange={setSortOrder}
        onFilterChange={setFilterCategory}
      />

      {/* Mobile Categories Button */}
      {isMobile && (
        <Sheet open={isMobileSidebarOpen} onOpenChange={setIsMobileSidebarOpen}>
          <SheetTrigger asChild>
            <Button
              variant="outline"
              className="mb-4 w-full flex justify-between items-center bg-background/40 backdrop-blur-sm border-white/10 h-12 text-white rounded-xl"
            >
              <span className="truncate mr-2">Categories: {selectedCategory}</span>
              <Filter className="h-4 w-4 flex-shrink-0 text-white/80" />
            </Button>
          </SheetTrigger>
          <SheetContent side="left" className="w-[280px] bg-background/95 backdrop-blur-md p-0">
            <div className="py-6 px-4">
              <h3 className="font-semibold mb-4 text-lg text-white">Categories</h3>
              <InventorySidebar 
                selectedCategory={selectedCategory}
                onCategorySelect={handleCategorySelect}
              />
            </div>
          </SheetContent>
        </Sheet>
      )}

      <div className="flex gap-4">
        {/* Desktop Sidebar */}
        {!isMobile && (
          <aside className="w-[240px] flex-shrink-0">
            <div className="sticky top-4 bg-card/30 backdrop-blur-sm border border-white/10 rounded-xl p-4">
              <InventorySidebar 
                selectedCategory={selectedCategory}
                onCategorySelect={handleCategorySelect}
              />
            </div>
          </aside>
        )}

        {/* Main content */}
        <main className="flex-1 min-w-0">
          <InventoryContent 
            selectedCategory={selectedCategory === "All" ? null : (selectedCategory as any)}
            searchQuery={searchTerm}
            showItemForm={isFormOpen}
            onCloseForm={handleCloseForm}
            setShowItemForm={setIsFormOpen}
            sortOrder={sortOrder}
            filterCategory={filterCategory}
          />
        </main>
      </div>
    </div>
  );
}
